import React, { useState, useEffect } from 'react';
import { 
  Container, Box, Typography, Paper, Grid, Table, TableBody, 
  TableCell, TableContainer, TableHead, TableRow, Tabs, Tab,
  Card, CardContent, Button, Divider, CircularProgress,
  useTheme, useMediaQuery, IconButton, alpha,
  TextField, Select, MenuItem, FormControl, InputLabel, InputAdornment
} from '@mui/material';
import { 
  PieChart, Pie, Cell, ResponsiveContainer, 
  Legend, Tooltip as RechartsTooltip
} from 'recharts';
import { PieChart as MuiPieChart } from '@mui/x-charts/PieChart';
import { chartPalette } from '../theme';
import { useSupabase } from '../contexts/SupabaseContext';
import { useNavigate } from 'react-router-dom';
import GuidedTour from '../components/tour/GuidedTour';
import TourButton from '../components/tour/TourButton';
import useTour from '../components/tour/useTour';
import { TOURS } from '../components/tour/tours';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';

// Define the asset classes and their products
const assetClasses = [
  {
    name: 'Equity',
    products: ['Stocks', 'Equity ETF', 'Mutual Fund Equity', 'NPS']
  },
  {
    name: 'Debt',
    products: ['Govt Bonds', 'Corp Bonds', 'NCD', 'FD', 'Mutual Fund', 'PPF', 'EPF', 'NPS', 'Others']
  },
  {
    name: 'Gold',
    products: ['Gold Bonds', 'Gold ETF', 'Digital Gold', 'Mutual Fund', 'Physical Gold']
  },
  {
    name: 'Real Estate',
    products: ['REIT', 'Real Estate Direct']
  },
  {
    name: 'Cash and Liquid',
    products: ['Liquid ETF', 'Savings Bank', 'SB Minimum Balance', 'Mutual Fund']
  },
  {
    name: 'Crypto',
    products: ['Bitcoin', 'Ethereum', 'Other Cryptocurrencies']
  },
  {
    name: 'Others',
    products: ['Alternative Investments', 'Collectibles', 'Other Assets']
  }
];

// Colors for the pie chart
const COLORS = [
  '#1976d2', '#2e7d32', '#f57c00', '#d32f2f', 
  '#9c27b0', '#0288d1', '#fbc02d', '#607d8b'
];

const Investments = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [loading, setLoading] = useState(true);
  const [investments, setInvestments] = useState([]);
  const [assetSummary, setAssetSummary] = useState([]);
  const [totalValue, setTotalValue] = useState(0);
  const { supabase, user } = useSupabase();
  const navigate = useNavigate();
  const tour = useTour('investments', { ready: !loading });
  
  const fetchInvestments = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      // Initialize totals for each asset class
      let equityTotal = 0;
      let debtTotal = 0;
      let goldTotal = 0;
      let realEstateTotal = 0;
      let cashAndLiquidTotal = 0;
      let cryptoTotal = 0;
      let othersTotal = 0;

      // Fetch Equity investments
      try {
        // Stocks
        const { data: stocksData, error: stocksError } = await supabase
          .from('stocks_investments')
          .select('amount_invested')
          .eq('user_id', user.id);
        if (!stocksError) {
          equityTotal += stocksData.reduce((sum, item) => sum + (Number(item.amount_invested) || 0), 0);
        } else {
          console.error('Error fetching stocks:', stocksError);
        }

        // Equity ETF
        const { data: equityETFData, error: equityETFError } = await supabase
          .from('equity_etf_investments')
          .select('amount_invested')
          .eq('user_id', user.id);
        if (!equityETFError) {
          equityTotal += equityETFData.reduce((sum, item) => sum + (Number(item.amount_invested) || 0), 0);
        } else {
          console.error('Error fetching equity ETF:', equityETFError);
        }

        // Equity Mutual Funds
        const { data: equityMFData, error: equityMFError } = await supabase
          .from('mutual_fund_equity_investments')
          .select('amount_invested')
          .eq('user_id', user.id);
        if (!equityMFError) {
          equityTotal += equityMFData.reduce((sum, item) => sum + (Number(item.amount_invested) || 0), 0);
        } else {
          console.error('Error fetching equity mutual funds:', equityMFError);
        }

        // NPS Equity
        const { data: npsEquityData, error: npsEquityError } = await supabase
          .from('nps_investments')
          .select('amount_invested')
          .eq('user_id', user.id);
        if (!npsEquityError) {
          equityTotal += npsEquityData.reduce((sum, item) => sum + (Number(item.amount_invested) || 0), 0);
        } else {
          console.error('Error fetching NPS:', npsEquityError);
        }

        console.log('Total equity value:', equityTotal);
      } catch (error) {
        console.error('Error fetching equity investments:', error);
      }

      // Fetch Debt investments
      try {
        // Government Bonds
        const { data: govtBondsData, error: govtBondsError } = await supabase
          .from('govt_bond_investments')
          .select('amount_invested')
          .eq('user_id', user.id);
        if (!govtBondsError) {
          debtTotal += govtBondsData.reduce((sum, item) => sum + (Number(item.amount_invested) || 0), 0);
        }

        // Corporate Bonds
        const { data: corpBondsData, error: corpBondsError } = await supabase
          .from('corp_bond_investments')
          .select('amount_invested')
          .eq('user_id', user.id);
        if (!corpBondsError) {
          debtTotal += corpBondsData.reduce((sum, item) => sum + (Number(item.amount_invested) || 0), 0);
        }

        // NCD
        const { data: ncdData, error: ncdError } = await supabase
          .from('ncd_investments')
          .select('amount_invested')
          .eq('user_id', user.id);
        if (!ncdError) {
          debtTotal += ncdData.reduce((sum, item) => sum + (Number(item.amount_invested) || 0), 0);
        }

        // Fixed Deposits
        const { data: fdData, error: fdError } = await supabase
          .from('fixed_deposit_investments')
          .select('amount_invested')
          .eq('user_id', user.id);
        if (!fdError) {
          debtTotal += fdData.reduce((sum, item) => sum + (Number(item.amount_invested) || 0), 0);
        }

        // Debt Mutual Funds
        const { data: debtMFData, error: debtMFError } = await supabase
          .from('debt_mutual_fund_investments')
          .select('amount_invested')
          .eq('user_id', user.id);
        if (!debtMFError) {
          debtTotal += debtMFData.reduce((sum, item) => sum + (Number(item.amount_invested) || 0), 0);
        }

        // PPF
        const { data: ppfData, error: ppfError } = await supabase
          .from('ppf_investments')
          .select('amount_invested')
          .eq('user_id', user.id);
        if (!ppfError) {
          debtTotal += ppfData.reduce((sum, item) => sum + (Number(item.amount_invested) || 0), 0);
        }

        // EPF
        const { data: epfData, error: epfError } = await supabase
          .from('epf_investments')
          .select('amount_invested')
          .eq('user_id', user.id);
        if (!epfError) {
          debtTotal += epfData.reduce((sum, item) => sum + (Number(item.amount_invested) || 0), 0);
        }

        // NPS Debt
        const { data: npsDebtData, error: npsDebtError } = await supabase
          .from('nps_debt_investments')
          .select('amount_invested')
          .eq('user_id', user.id);
        if (!npsDebtError) {
          debtTotal += npsDebtData.reduce((sum, item) => sum + (Number(item.amount_invested) || 0), 0);
        }

        // Debt Others
        const { data: debtOthersData, error: debtOthersError } = await supabase
          .from('debt_others_investments')
          .select('amount_invested')
          .eq('user_id', user.id);
        if (!debtOthersError) {
          debtTotal += debtOthersData.reduce((sum, item) => sum + (Number(item.amount_invested) || 0), 0);
        }

        // Fetch Recurring Deposits
        const { data: rdData, error: rdError } = await supabase
          .from('recurring_deposit_investments')
          .select('monthly_investment, start_date')
          .eq('user_id', user.id);
        if (!rdError) {
          // Calculate current contribution value for each RD and sum
          const rdTotal = rdData.reduce((sum, item) => {
            const start = new Date(item.start_date);
            const today = new Date();
            
            // Calculate months difference
            const monthsDiff = (today.getFullYear() - start.getFullYear()) * 12 + 
                            (today.getMonth() - start.getMonth());
            
            // If start date is in future, return 0
            if (monthsDiff < 0) return sum;
            
            // If start date day is greater than today's date, subtract one month
            // as the current month's installment wouldn't be counted
            const adjustedMonths = start.getDate() > today.getDate() ? monthsDiff : monthsDiff + 1;
            
            // Calculate total contribution for this RD
            const contribution = item.monthly_investment * Math.max(0, adjustedMonths);
            return sum + contribution;
          }, 0);
          debtTotal += rdTotal;
        }
      } catch (error) {
        console.error('Error fetching debt investments:', error);
      }

      // Fetch Gold investments
      try {
        // Gold Bonds
        const { data: goldBondsData, error: goldBondsError } = await supabase
          .from('gold_bond_investments')
          .select('amount_invested')
          .eq('user_id', user.id);
        if (!goldBondsError) {
          goldTotal += goldBondsData.reduce((sum, item) => sum + (Number(item.amount_invested) || 0), 0);
        }

        // Gold ETF
        const { data: goldETFData, error: goldETFError } = await supabase
          .from('gold_etf_investments')
          .select('amount_invested')
          .eq('user_id', user.id);
        if (!goldETFError) {
          goldTotal += goldETFData.reduce((sum, item) => sum + (Number(item.amount_invested) || 0), 0);
        }

        // Digital Gold
        const { data: digitalGoldData, error: digitalGoldError } = await supabase
          .from('digital_gold_investments')
          .select('amount_invested')
          .eq('user_id', user.id);
        if (!digitalGoldError) {
          goldTotal += digitalGoldData.reduce((sum, item) => sum + (Number(item.amount_invested) || 0), 0);
        }

        // Gold Mutual Funds
        const { data: goldMFData, error: goldMFError } = await supabase
          .from('gold_mutual_fund_investments')
          .select('amount_invested')
          .eq('user_id', user.id);
        if (!goldMFError) {
          goldTotal += goldMFData.reduce((sum, item) => sum + (Number(item.amount_invested) || 0), 0);
        }

        // Physical Gold
        const { data: physicalGoldData, error: physicalGoldError } = await supabase
          .from('physical_gold_investments')
          .select('amount_invested')
          .eq('user_id', user.id);
        if (!physicalGoldError) {
          goldTotal += physicalGoldData.reduce((sum, item) => sum + (Number(item.amount_invested) || 0), 0);
        }
      } catch (error) {
        console.error('Error fetching gold investments:', error);
      }

      // Fetch Real Estate investments
      try {
        // REIT
        const { data: reitData, error: reitError } = await supabase
          .from('reit_investments')
          .select('amount_invested')
        .eq('user_id', user.id);
        if (!reitError) {
          realEstateTotal += reitData.reduce((sum, item) => sum + (Number(item.amount_invested) || 0), 0);
        }

        // Direct Real Estate
        const { data: directREData, error: directREError } = await supabase
          .from('real_estate_direct_investments')
          .select('price')
          .eq('user_id', user.id);
        if (!directREError) {
          realEstateTotal += directREData.reduce((sum, item) => sum + (Number(item.price) || 0), 0);
        }
      } catch (error) {
        console.error('Error fetching real estate investments:', error);
      }

      // Fetch Cash and Liquid investments
      try {
        // Savings Bank
      const { data: savingsData, error: savingsError } = await supabase
        .from('wallets')
        .select('balance')
        .eq('user_id', user.id)
        .eq('type', 'bank')
        .not('is_archived', 'is', true); // hide archived bank accounts
        if (!savingsError) {
          cashAndLiquidTotal += savingsData.reduce((sum, item) => sum + (Number(item.balance) || 0), 0);
        }

        // Liquid ETF
        const { data: liquidETFData, error: liquidETFError } = await supabase
          .from('liquid_etf_investments')
          .select('amount_invested')
          .eq('user_id', user.id);
        if (!liquidETFError) {
          cashAndLiquidTotal += liquidETFData.reduce((sum, item) => sum + (Number(item.amount_invested) || 0), 0);
        }

        // Liquid Mutual Funds
        const { data: liquidMFData, error: liquidMFError } = await supabase
          .from('mutual_fund_investments')
          .select('amount_invested')
          .eq('user_id', user.id);
        if (!liquidMFError) {
          cashAndLiquidTotal += liquidMFData.reduce((sum, item) => sum + (Number(item.amount_invested) || 0), 0);
        }
      } catch (error) {
        console.error('Error fetching cash and liquid investments:', error);
      }

      // Fetch Crypto investments
      try {
        const { data: cryptoData, error: cryptoError } = await supabase
          .from('crypto_investments')
          .select('amount_invested')
          .eq('user_id', user.id);
        if (!cryptoError) {
          cryptoTotal = cryptoData.reduce((sum, item) => sum + (Number(item.amount_invested) || 0), 0);
        }
      } catch (error) {
        console.error('Error fetching crypto investments:', error);
      }

      // Fetch Other investments
      try {
        const { data: othersData, error: othersError } = await supabase
          .from('other_investments')
          .select('amount_invested')
          .eq('user_id', user.id);
        if (!othersError) {
          othersTotal = othersData.reduce((sum, item) => sum + (Number(item.amount_invested) || 0), 0);
        }
      } catch (error) {
        console.error('Error fetching other investments:', error);
      }

      // Create summary of all asset classes
      const summary = [
        {
          name: 'Equity',
          value: equityTotal,
          path: '/investments/equity'
        },
        {
          name: 'Debt',
          value: debtTotal,
          path: '/investments/debt'
        },
        {
          name: 'Gold',
          value: goldTotal,
          path: '/investments/gold'
        },
        {
          name: 'Real Estate',
          value: realEstateTotal,
          path: '/investments/real-estate'
        },
        {
          name: 'Cash and Liquid',
          value: cashAndLiquidTotal,
          path: '/investments/cash-and-liquid'
        },
        {
          name: 'Crypto',
          value: cryptoTotal,
          path: '/investments/crypto'
        },
        {
          name: 'Others',
          value: othersTotal,
          path: '/investments/others'
        }
      ];

      // Sort by value (highest to lowest)
      const sortedSummary = summary
        .sort((a, b) => b.value - a.value)
        .map((item, index) => ({
          ...item,
          color: COLORS[index % COLORS.length],
          percentage: item.value
        }));
      
      setAssetSummary(sortedSummary);
      const total = sortedSummary.reduce((sum, item) => sum + item.value, 0);
      setTotalValue(total);

      // Update percentages after total is calculated
      setAssetSummary(sortedSummary.map(item => ({
        ...item,
        percentage: total > 0 ? (item.value / total) * 100 : 0
      })));
      
    } catch (error) {
      console.error('Error fetching investments:', error);
      setAssetSummary([]);
      setTotalValue(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInvestments();
  }, [supabase, user]);
  
  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Format percentage
  const formatPercentage = (value) => {
    return `${value.toFixed(2)}%`;
  };
  
  // Custom tooltip for pie chart
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <Paper sx={{ p: 1, boxShadow: 2 }}>
          <Typography variant="body2" fontWeight="bold">
            {payload[0].name}
          </Typography>
          <Typography variant="body2">
            {formatCurrency(payload[0].value)}
          </Typography>
          <Typography variant="body2">
            {formatPercentage(payload[0].payload.percentage)}
          </Typography>
        </Paper>
      );
    }
    
    return null;
  };
  
  const handleRowClick = (assetClass) => {
    navigate(`/investments/${assetClass.toLowerCase().replace(/\s+/g, '-')}`);
  };

  return (
    <>
      <Box sx={{ 
        p: { xs: 1, sm: 2, md: 4 },
        minHeight: '100vh'
      }}>
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: 2,
          mb: { xs: 2, sm: 3 }
        }}>
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 700,
              fontSize: { xs: '1.5rem', sm: '2rem' },
              color: 'text.primary',
            }}
          >
            Investments
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <TourButton onClick={tour.start} />
            <Button variant="outlined" startIcon={<InsightsOutlinedIcon />} onClick={() => navigate('/analysis?view=investments')} data-tour="inv-analysis-link">
              Analysis
            </Button>
          </Box>
        </Box>
        
        {/* Summary Cards */}
        <Grid container spacing={2} sx={{ mb: 3 }} data-tour="inv-summary">
          <Grid item xs={12} sm={6}>
            <Card sx={{
              borderRadius: '16px',
              background: theme => alpha(theme.palette.background.paper, 0.6),
              backdropFilter: 'blur(10px)',
              boxShadow: 'var(--fx-card-shadow)'
            }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Total Investments
                </Typography>
                <Typography variant="h5" component="div" sx={{ fontWeight: 600 }}>
                  {formatCurrency(totalValue)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <Card sx={{
              borderRadius: '16px',
              background: theme => alpha(theme.palette.background.paper, 0.6),
              backdropFilter: 'blur(10px)',
              boxShadow: 'var(--fx-card-shadow)'
            }}>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Most Valuable Asset Class
                </Typography>
                <Typography variant="h5" component="div" sx={{ fontWeight: 600 }}>
                  {assetSummary.length > 0 ? 
                    assetSummary.sort((a, b) => b.value - a.value)[0].name : 
                    'N/A'
                  }
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={3}>
            {/* Asset Class Table */}
            <Grid item xs={12} md={6} lg={7}>
              <Paper data-tour="inv-allocation" sx={{ 
                p: { xs: 2, sm: 3 }, 
                height: '100%',
                borderRadius: { xs: '16px', sm: '24px' },
                background: theme => alpha(theme.palette.background.paper, 0.4),
                backdropFilter: 'blur(10px)',
                boxShadow: 'var(--fx-card-shadow)'
              }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    mb: 3,
                    fontWeight: 600
                  }}
                >
                  Asset Allocation
                </Typography>
                
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell 
                          sx={{ 
                            fontWeight: 600,
                            color: theme => theme.palette.text.primary,
                            borderBottom: theme => `2px solid ${theme.palette.divider}`
                          }}
                        >
                          Asset Class
                        </TableCell>
                        <TableCell 
                          align="right"
                          sx={{ 
                            fontWeight: 600,
                            color: theme => theme.palette.text.primary,
                            borderBottom: theme => `2px solid ${theme.palette.divider}`
                          }}
                        >
                          Value
                        </TableCell>
                        <TableCell 
                          align="right"
                          sx={{ 
                            fontWeight: 600,
                            color: theme => theme.palette.text.primary,
                            borderBottom: theme => `2px solid ${theme.palette.divider}`
                          }}
                        >
                          In %
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {assetSummary.map((row) => (
                        <TableRow 
                          key={row.name} 
                          onClick={() => handleRowClick(row.name)}
                          sx={{ 
                            '&:last-child td, &:last-child th': { border: 0 },
                            borderLeft: `4px solid ${row.color}`,
                            opacity: row.value === 0 ? 0.6 : 1,
                            '&:hover': {
                              backgroundColor: theme => alpha(theme.palette.action.hover, 0.1),
                              cursor: 'pointer'
                            }
                          }}
                        >
                          <TableCell 
                            component="th" 
                            scope="row"
                            sx={{
                              py: 2,
                              fontSize: '0.875rem',
                              fontWeight: 500
                            }}
                          >
                            {row.name}
                            {row.value === 0 && (
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  ml: 1,
                                  color: 'text.secondary',
                                  fontSize: '0.75rem'
                                }}
                              >
                                (No investments)
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell 
                            align="right"
                            sx={{
                              py: 2,
                              fontSize: '0.875rem',
                              fontWeight: row.value > 0 ? 500 : 400
                            }}
                          >
                            {formatCurrency(row.value)}
                          </TableCell>
                          <TableCell 
                            align="right"
                            sx={{
                              py: 2,
                              fontSize: '0.875rem',
                              fontWeight: row.value > 0 ? 500 : 400
                            }}
                          >
                            {formatPercentage(row.percentage)}
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow 
                        sx={{ 
                          backgroundColor: theme => alpha(theme.palette.action.hover, 0.1),
                          borderTop: theme => `2px solid ${theme.palette.divider}`
                        }}
                      >
                        <TableCell 
                          component="th" 
                          scope="row"
                          sx={{
                            py: 2,
                            fontSize: '0.875rem',
                            fontWeight: 600
                          }}
                        >
                          Total
                        </TableCell>
                        <TableCell 
                          align="right"
                          sx={{
                            py: 2,
                            fontSize: '0.875rem',
                            fontWeight: 600
                          }}
                        >
                          {formatCurrency(totalValue)}
                        </TableCell>
                        <TableCell 
                          align="right"
                          sx={{
                            py: 2,
                            fontSize: '0.875rem',
                            fontWeight: 600
                          }}
                        >
                          100.00%
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
            
            {/* Pie Chart */}
            <Grid item xs={12} md={6} lg={5}>
              <Paper data-tour="inv-distribution" sx={{ 
                height: '100%', 
                borderRadius: 3,
                bgcolor: 'background.paper',
                boxShadow: 'var(--fx-card-shadow)',
                display: 'flex',
                flexDirection: 'column'
              }}>
                <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Distribution
                  </Typography>
                </Box>
                <Box sx={{ 
                  flexGrow: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 2
                }}>
                  {assetSummary.length > 0 ? (
                    <MuiPieChart colors={chartPalette}
                      series={[{
                        data: assetSummary.map(item => ({
                          id: item.name,
                          value: item.value,
                          label: item.name
                        })),
                        highlightScope: { faded: 'global', highlighted: 'item' },
                        faded: { innerRadius: 30, additionalRadius: -30 },
                        // Hide labels and blocks
                        label: false,
                        labelLine: false,
                      }]}
                      height={300}
                      // Hide legend
                      legend={{
                        hidden: true
                      }}
                      margin={{
                        top: 0,
                        right: 0,
                        bottom: 0,
                        left: 0
                      }}
                    />
                  ) : (
                    <Typography variant="body1" color="text.secondary">
                      No data available
                    </Typography>
                  )}
                </Box>
              </Paper>
            </Grid>
          </Grid>
        )}
      </Box>
      <GuidedTour steps={TOURS.investments} open={tour.open} onClose={tour.close} />
    </>
  );
};

export default Investments;